import { createDeal } from "@/api/deals-api";
import { isIndustriesPendingApproval } from "@/lib/deal-field-state";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { TrafficGrowthCell } from "@/components/deals/traffic-growth-cell";
import { cn, formatTimeAgo, resolveIndustryLabel } from "@/lib/utils";
import { isDevS3File } from "@/lib/url-utils";
import { getLastActivityDate } from "@/services/deals-service";
import { useAuthStore } from "@/store/auth-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useDealsViewStore, type SortField } from "@/store/deals-view-store";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle,
  Gem,
  Loader2,
  MoreVertical,
} from "lucide-react";
import { CompanyAvatar } from "./company-avatar";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { DeleteDialog } from "./delete-dialog";
import { DeactivateDealDialog } from "./deactivate-deal-dialog";
import { QuickActionsToolbar } from "./quick-actions-toolbar";
import { AddToBoardSubMenu } from "./add-to-board-menu";
import { resolveVisibleFields } from "./fields";
import { GenericCell } from "./generic-cell";
import { useRunAiCell } from "./use-run-ai-cell";
import { AiColumnCell, type AiCellValue } from "./ai-column-cell";
import type { AiColumn } from "@/api/deals-api";
import { Sparkles } from "lucide-react";
import {
  compareDeals,
  getFitScore,
  getReportStatusIndicator,
  hasFitScore,
} from "./utils";

interface TableViewProps {
  deals: DealTransformed[];
  /** Workspace AI columns, rendered after the registry columns. */
  aiColumns?: AiColumn[];
  onAiColumnRun?: () => void;
  onStatusChange: (params: { dealId: string; status: string }) => void;
  workspaceId: string;
  onDealDeleted?: () => void;
  onDealArchived?: () => void;
}

interface CompanyFile {
  _id: string;
  file_type: string;
  file_url: string;
  file_size: string;
  time_stamp: string;
  company: string;
}

const getPitchdeckFile = (files: CompanyFile[]) => {
  return files.find(
    (file) =>
      file.file_type === "company pitchdeck" && !isDevS3File(file.file_url)
  );
};

/**
 * Fields with hand-written cells in this table. Everything else in the registry
 * falls through to `GenericCell`.
 *
 * These carry links, loading states, inline dropdowns and per-cell tooltips that
 * a generic renderer would flatten, so they stay as they are.
 */
const BESPOKE_CELL_IDS = new Set([
  "name",
  "fitScore",
  "industry",
  "traffic",
  "source",
  "status",
  "lastActivity",
]);

// Sort-header arrow. Active field shows direction; inactive shows muted bidirectional.
function SortArrow({
  field,
  currentField,
  direction,
}: {
  field: SortField;
  currentField: SortField;
  direction: "asc" | "desc";
}) {
  if (field !== currentField) {
    return (
      <ArrowUpDown className="inline h-4 w-4 ml-2 text-muted-foreground/40" />
    );
  }
  return direction === "asc" ? (
    <ArrowUp className="inline h-4 w-4 ml-2" />
  ) : (
    <ArrowDown className="inline h-4 w-4 ml-2" />
  );
}

export const TableView = ({
  deals,
  aiColumns: allAiColumns = [],
  onAiColumnRun,
  onStatusChange,
  workspaceId,
  onDealDeleted,
  onDealArchived,
}: TableViewProps) => {
  const { user } = useAuthStore();
  const { activeWorkspace } = useWorkspaceStore();
  const { sort, setSort, toggleSortDirection, visibleColumns, hiddenAiColumnIds } =
    useDealsViewStore();

  // Switched off in the columns menu. Hiding a column does not stop its cells
  // existing — a hidden column that has already been run keeps its answers, and
  // showing it again brings them straight back.
  const aiColumns = useMemo(
    () => allAiColumns.filter((column) => !hiddenAiColumnIds.includes(column._id)),
    [allAiColumns, hiddenAiColumnIds]
  );

  const runAiCell = useRunAiCell({ deals, workspaceId, onAiColumnRun });

  const [selectedDealIds, setSelectedDealIds] = useState<Set<string>>(
    new Set()
  );

  const statusPriority = useMemo(() => {
    return (activeWorkspace?.statuses || []).reduce(
      (acc: Record<string, number>, status, index: number) => ({
        ...acc,
        [status.title]: index,
      }),
      {} as Record<string, number>
    );
  }, [activeWorkspace?.statuses]);

  // Fit Score column renders only if at least one deal in view has been
  // analyzed. Gap Analysis is hidden from the table — too dense for daily
  // scanning; it's still visible on the deal detail page.
  const showFitScoreColumn = useMemo(() => deals.some(hasFitScore), [deals]);

  // Columns come from the registry, filtered by the user's choice. The
  // fit-score rule is applied on top rather than folded into the registry: it
  // depends on the deals currently in view, not on configuration, so a user who
  // has explicitly enabled the column still shouldn't see an empty one.
  const visibleFields = useMemo(
    () =>
      resolveVisibleFields(visibleColumns ?? undefined).filter(
        (field) => field.id !== "fitScore" || showFitScoreColumn
      ),
    [visibleColumns, showFitScoreColumn]
  );

  const visibleIds = useMemo(
    () => new Set(visibleFields.map((f) => f.id)),
    [visibleFields]
  );
  const isVisible = (id: string) => visibleIds.has(id);

  // Fields with no bespoke cell below get a generic one. They are rendered
  // after the hand-written cells, which stays aligned with the header because
  // BESPOKE_CELL_IDS all sort before the extras in the registry — asserted in
  // fields.test.ts so a reordering there fails loudly rather than shifting
  // every row by one column.
  const genericFields = useMemo(
    () => visibleFields.filter((f) => !BESPOKE_CELL_IDS.has(f.id)),
    [visibleFields]
  );

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({
    isOpen: false,
    dealId: "",
    dealName: "",
  });

  const [deactivateDialog, setDeactivateDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({
    isOpen: false,
    dealId: "",
    dealName: "",
  });

  const sortedDeals = useMemo(() => {
    return [...deals].sort((a, b) =>
      compareDeals(a, b, sort.field, sort.direction, { statusPriority })
    );
  }, [deals, sort, statusPriority]);

  const handleSort = (field: SortField) => {
    if (sort.field === field) {
      toggleSortDirection();
    } else {
      setSort({ field, direction: field === "name" ? "asc" : "desc" });
    }
  };

  const handleArchive = async (dealId: string) => {
    if (!workspaceId) return;
    try {
      await createDeal.createFlaggedCompany(
        workspaceId,
        dealId,
        user?.id ?? ""
      );
      onDealArchived?.();
      toast.success("Deal archived successfully");
    } catch (error) {
      console.error("Failed to archive deal:", error);
      toast.error(getErrorMessage(error, "Failed to archive deal"));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDealIds(new Set(sortedDeals.map((deal) => deal.id)));
    } else {
      setSelectedDealIds(new Set());
    }
  };

  const handleSelectDeal = (dealId: string, checked: boolean) => {
    const newSelection = new Set(selectedDealIds);
    if (checked) {
      newSelection.add(dealId);
    } else {
      newSelection.delete(dealId);
    }
    setSelectedDealIds(newSelection);
  };

  const clearSelection = () => {
    setSelectedDealIds(new Set());
  };

  const isAllSelected =
    selectedDealIds.size === sortedDeals.length && sortedDeals.length > 0;
  const isIndeterminate =
    selectedDealIds.size > 0 && selectedDealIds.size < sortedDeals.length;

  return (
    <div className="h-full overflow-hidden w-full p-4">
      <div className="flex h-full flex-col rounded-md border border-border bg-card overflow-hidden">
        <div className="flex-1 overflow-auto">
      <Table className="outline-none border-none w-full" tabIndex={-1}>
        <TableHeader className="[&_tr]:border-b [&_tr]:border-border sticky top-0 z-10 bg-card">
          <TableRow className="border-b border-border [&_th]:text-[10px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-widest [&_th]:text-muted-foreground/80 [&_th]:whitespace-nowrap [&_th_button]:uppercase [&_th_button]:tracking-widest">
            <TableHead className="w-12">
              <div className="relative inline-flex">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all deals"
                  className={
                    isIndeterminate
                      ? "border-foreground bg-foreground text-background shadow-none"
                      : "border-border shadow-none data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background"
                  }
                />
                {isIndeterminate && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="h-0.5 w-2 rounded-sm bg-background" />
                  </div>
                )}
              </div>
            </TableHead>
            {visibleFields.map((field) => (
              <TableHead
                key={field.id}
                onClick={
                  field.sortable ? () => handleSort(field.id) : undefined
                }
                className={cn(
                  field.responsiveClass,
                  field.sortable && "cursor-pointer hover:bg-muted"
                )}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      className={field.sortable ? "flex items-center" : undefined}
                    >
                      {field.label}
                      {field.sortable && (
                        <SortArrow
                          field={field.id}
                          currentField={sort.field}
                          direction={sort.direction}
                        />
                      )}
                    </TooltipTrigger>
                    {field.description && (
                      <TooltipContent>
                        <p className="max-w-[220px]">{field.description}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
            ))}
            {aiColumns.map((column) => (
              <TableHead key={column._id}>
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-primary" />
                  {column.name}
                </span>
              </TableHead>
            ))}
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDeals.map((deal) => {
            const pitchdeckFile = getPitchdeckFile(deal.company_files || []);
            const statusIndicator = getReportStatusIndicator(
              deal.reportStatus?.opportunityApplication?.report_status
            );
            const statusColor = activeWorkspace?.statuses.find(
              (s) => s.title === deal.overview.status
            )?.colour;
            // Leading-dot fill from workspace status color. Per DESIGN.md
            // status-badge spec + absolute ban on side-stripe borders.
            const statusDotStyle = statusColor?.startsWith("#")
              ? { backgroundColor: statusColor }
              : undefined;

            return (
              <TableRow
                key={deal.id}
                className="group border-b border-border/40 transition-colors hover:bg-foreground/[0.02]"
              >
                <TableCell className="w-12">
                  <Checkbox
                    checked={selectedDealIds.has(deal.id)}
                    onCheckedChange={(checked) =>
                      handleSelectDeal(deal.id, checked as boolean)
                    }
                    aria-label={`Select ${deal.overview.name}`}
                    className="border-border shadow-none data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background"
                  />
                </TableCell>
                {isVisible("name") && (
                <TableCell className="max-w-[300px] px-6 py-2">
                  <Link
                    to={`/${workspaceId}/deals/${deal.id}`}
                    className="block"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <CompanyAvatar
                        companyFiles={deal.company_files}
                        websiteUrl={deal.overview.website}
                        alt={deal.overview.name}
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1">
                          <span className="text-sm font-medium text-foreground group-hover:underline decoration-muted-foreground/30 underline-offset-2 truncate">
                            {deal.overview.name}
                          </span>
                          {deal.aiTracked && (
                            <Gem className="h-3.5 w-3.5 shrink-0 text-primary" />
                          )}
                          {deal.prospectiveAffEmail && (
                            <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />
                          )}
                          {statusIndicator && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex shrink-0">
                                    <statusIndicator.icon
                                      className={`h-3.5 w-3.5 ${
                                        statusIndicator.type === "loading"
                                          ? "text-blue-500 animate-spin"
                                          : "text-red-500"
                                      }`}
                                    />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {statusIndicator.label}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      </span>
                    </span>
                  </Link>
                </TableCell>
                )}

                {isVisible("fitScore") && (
                  <TableCell>
                    {hasFitScore(deal) ? (
                      <span className="font-mono text-sm font-medium tabular-nums text-foreground">
                        {Math.round(getFitScore(deal) as number)}
                        <span className="text-muted-foreground/50 font-normal ml-0.5">
                          /100
                        </span>
                      </span>
                    ) : statusIndicator?.type === "loading" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                        Analyzing…
                      </span>
                    ) : statusIndicator?.type === "failed" ? (
                      <Link
                        to={`/${workspaceId}/deals/${deal.id}?tab=analysis`}
                        className="text-xs text-red-500 hover:underline underline-offset-2 whitespace-nowrap"
                      >
                        Analysis failed
                      </Link>
                    ) : (
                      <Link
                        to={`/${workspaceId}/deals/${deal.id}?tab=analysis`}
                        className="text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-2 whitespace-nowrap"
                      >
                        Run analysis →
                      </Link>
                    )}
                  </TableCell>
                )}

                {isVisible("industry") && (
                <TableCell className="hidden lg:table-cell px-2 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    {isIndustriesPendingApproval(deal) ? (
                      <Link
                        to={`/${workspaceId}/deals/${deal.id}?tab=profile#field-industries`}
                        className="inline-flex items-center gap-1.5 rounded-sm bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-200 dark:hover:bg-purple-900/40"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                        Review industries
                      </Link>
                    ) : deal.overview.industry?.length > 0 ? (
                      <>
                        {(() => {
                          // Resolve GICS codes → labels, then de-dupe and
                          // drop any "—" placeholders from unknown codes so
                          // the chip row only shows real industry names.
                          const labels = Array.from(
                            new Set(
                              deal.overview.industry
                                .map(resolveIndustryLabel)
                                .filter((l) => l && l !== "—")
                            )
                          );
                          if (labels.length === 0) {
                            return (
                              <span className="text-xs text-muted-foreground/40">
                                —
                              </span>
                            );
                          }
                          return (
                            <>
                              {labels.slice(0, 2).map((label, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-xs text-foreground"
                                >
                                  {label}
                                </span>
                              ))}
                              {labels.length > 2 && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-xs text-muted-foreground cursor-default">
                                        +{labels.length - 2}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-[280px]">
                                        {labels.slice(2).join(", ")}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground/40">
                        —
                      </span>
                    )}
                  </div>
                </TableCell>
                )}

                {isVisible("traffic") && (
                <TableCell className="hidden xl:table-cell px-2 py-2">
                  <TrafficGrowthCell growth={deal.websiteTrafficGrowth} />
                </TableCell>
                )}

                {isVisible("source") && (
                <TableCell className="px-2 py-2">
                  <span className="text-xs text-muted-foreground/80">
                    {deal.overview.source || (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </span>
                </TableCell>
                )}

                {isVisible("status") && (
                <TableCell className="px-2 py-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="inline-flex items-center gap-1.5 rounded-sm bg-input/60 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-widest leading-none text-foreground transition-colors hover:bg-input"
                      >
                        <span
                          aria-hidden
                          className={
                            statusDotStyle
                              ? "h-1.5 w-1.5 rounded-full"
                              : "h-1.5 w-1.5 rounded-full bg-muted-foreground/50"
                          }
                          style={statusDotStyle}
                        />
                        {deal.overview.status}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuRadioGroup
                        value={deal.overview.status}
                        onValueChange={(value) => {
                          onStatusChange({
                            dealId: deal.id,
                            status: value,
                          });
                        }}
                      >
                        {(activeWorkspace?.statuses || []).map((status) => {
                          const itemDotStyle = status.colour?.startsWith("#")
                            ? { backgroundColor: status.colour }
                            : undefined;
                          return (
                            <DropdownMenuRadioItem
                              key={status.title}
                              value={status.title}
                              className="pl-2 [&>span:first-child]:hidden"
                            >
                              <span
                                aria-hidden
                                className={
                                  itemDotStyle
                                    ? "mr-2 h-1.5 w-1.5 shrink-0 rounded-full"
                                    : "mr-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50"
                                }
                                style={itemDotStyle}
                              />
                              {status.title}
                            </DropdownMenuRadioItem>
                          );
                        })}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
                )}

                {isVisible("lastActivity") && (
                <TableCell className="hidden lg:table-cell px-2 py-2">
                  <span className="font-mono text-xs tabular-nums text-muted-foreground/60">
                    {formatTimeAgo(getLastActivityDate(deal))}
                  </span>
                </TableCell>
                )}

                {genericFields.map((field) => (
                  <TableCell
                    key={field.id}
                    className={cn("px-4 py-2", field.responsiveClass)}
                  >
                    <GenericCell field={field} deal={deal} />
                  </TableCell>
                ))}

                {aiColumns.map((column) => (
                  <TableCell key={column._id} className="px-4 py-2">
                    <AiColumnCell
                      type={column.type}
                      cell={
                        (deal.aiColumnValues ?? {})[column._id] as
                          | AiCellValue
                          | undefined
                      }
                      onRun={() => runAiCell(deal, column._id)}
                    />
                  </TableCell>
                ))}


                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="px-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {pitchdeckFile && !isDevS3File(pitchdeckFile.file_url) && (
                        <>
                          <Dialog>
                            <DialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                              >
                                View Pitchdeck
                              </DropdownMenuItem>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl h-[80vh]">
                              <div className="relative w-full h-full">
                                <iframe
                                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                                    pitchdeckFile.file_url
                                  )}&embedded=true`}
                                  className="w-full h-full"
                                  title="Pitchdeck Viewer"
                                />
                              </div>
                            </DialogContent>
                          </Dialog>
                          <DropdownMenuItem
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = pitchdeckFile.file_url;
                              link.download = "pitchdeck.pdf";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            Download Pitchdeck
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <AddToBoardSubMenu companyIds={[deal.id]} />
                      {(() => {
                        const portfolioStatus = activeWorkspace?.statuses.find(
                          (s) =>
                            s.title.toLowerCase().includes("portfolio")
                        )?.title;
                        if (
                          !portfolioStatus ||
                          deal.overview.status === portfolioStatus
                        )
                          return null;
                        return (
                          <DropdownMenuItem
                            onClick={() =>
                              onStatusChange({
                                dealId: deal.id,
                                status: portfolioStatus,
                              })
                            }
                          >
                            Add to Portfolio
                          </DropdownMenuItem>
                        );
                      })()}
                      {deal.aiTracked && (
                        <DropdownMenuItem
                          onClick={() =>
                            setDeactivateDialog({
                              isOpen: true,
                              dealId: deal.id,
                              dealName: deal.overview.name,
                            })
                          }
                        >
                          Mark as not premium
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleArchive(deal.id)}
                        className="text-yellow-600"
                      >
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() =>
                          setDeleteDialog({
                            isOpen: true,
                            dealId: deal.id,
                            dealName: deal.overview.name,
                          })
                        }
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
        </div>
      </div>

      {selectedDealIds.size > 0 && (
        <QuickActionsToolbar
          selectedDealIds={Array.from(selectedDealIds)}
          onClearSelection={clearSelection}
          onDealDeleted={onDealDeleted}
          onDealArchived={onDealArchived}
          workspaceId={workspaceId}
        />
      )}

      <DeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() =>
          setDeleteDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deleteDialog.dealId}
        dealName={deleteDialog.dealName}
        workspaceId={workspaceId}
        onSuccess={() => {
          onDealDeleted?.();
        }}
      />

      <DeactivateDealDialog
        isOpen={deactivateDialog.isOpen}
        onClose={() =>
          setDeactivateDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deactivateDialog.dealId}
        dealName={deactivateDialog.dealName}
        workspaceId={workspaceId}
      />
    </div>
  );
};
